<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { ProductSpec } from '@/api/ls'

/**
 * entitlement map 選擇器（膠囊式 UX，對齊 CS AdminLicense）。
 *
 * ★★ 批93 整支改寫成**資料驅動**：列從 `props.spec.modules` 來，不再是逐一手寫的 ref。
 *
 * 改寫的理由不是行數——是消滅一整族 bug：舊版每個二態模組都要在**五處**手寫
 * （ref 宣告／`initFrom`／`build()`／`watch` 陣列／template 膠囊列），其中漏掉
 * `build()` 最危險：它是白名單重建，管理員只是打開既有授權按「重簽」，那個鍵就被
 * **靜默刪掉**（＝客戶模組無聲關閉）。批81 的 `api`、批83 的 `calendar` 都踩過。
 * 現在鍵集合來自憑證設計，結構上不可能漏。
 *
 * 兩個必守的呼叫端約定：
 * 1. **`initFrom` 只在掛載時跑**——父層帶入新值（套用套裝／換客戶／**換產品**）一律
 *    以 `:key` 重掛本元件，否則畫面停在舊值而送出的是新值。
 * 2. **換產品必 bump `:key`**：不同產品的模組鍵完全不同，沿用舊狀態會把上一個產品的
 *    勾選帶進新證。
 *
 * `spec` 為 null（還沒載回來）時只顯示提示、**原樣回吐 `modelValue`**——絕不因為
 * 「暫時不知道有哪些模組」就把整包模組清空。
 *
 * ## 批155：內部模組（`ModuleSpec.internal`）
 *
 * 內部模組只有營運主管以上能放進核發／換證／審核改內容（後端 403
 * `internal_plan_requires_manager`），所以呼叫端傳 `allowInternal`（我方頁面＝`auth.isManagerOrAbove`；
 * 代理商與兌換碼表單不傳＝不列）。不允許時：
 * - 起手值**沒有**它 → 這一列不畫（「其他角色不列」）；
 * - 起手值**已經有**它（例如換發一張本來就帶著它的證）→ 照畫但**鎖住**，鍵原樣保留——
 *   靜默藏起來又照送是最難查的那種（畫面上沒有、送出去卻有），靜默刪掉則是本元件最早
 *   那個 bug（模組無聲關閉）。能不能簽由後端決定。
 */
const props = defineProps<{
  modelValue: Record<string, string>
  /** 該產品的憑證設計；null＝尚未載入（本元件退化成唯讀提示）。 */
  spec: ProductSpec | null
  /** 批155：可以勾內部模組（營運主管以上）。缺省＝不可以。 */
  allowInternal?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [Record<string, string>] }>()

type Mode = 'none' | 'all' | 'pick'
interface RowState {
  mode: Mode
  items: string[]
}

const modules = computed(() => props.spec?.modules ?? [])
/** 本元件認得的鍵（＝憑證設計裡有的）。其餘視為「未知鍵」原樣保留，見下。 */
const knownKeys = computed(() => new Set(modules.value.map((m) => m.key)))

const state = ref<Record<string, RowState>>({})
/**
 * 授權上有、但這個產品的憑證設計沒有的鍵（打錯字／別的產品的殘留）。
 *
 * ★ **原樣保留、不靜默刪除**：刪掉是舊版最惡的那個 bug（客戶模組無聲關閉）。畫面上以
 *   一行警示說明它們的存在，操作者要清掉請走「套用套裝」或直接改申請內容。
 *   後端核發時也會回 `unknown_modules` 警告，兩邊互相印證。
 */
const unknownEntries = ref<Record<string, string>>({})
/** 批155：起手值就帶著的內部模組（不允許勾內部模組時，這幾列照畫但鎖住）。 */
const presetInternal = ref<Set<string>>(new Set())

function initFrom(m: Record<string, string>) {
  const next: Record<string, RowState> = {}
  presetInternal.value = new Set(
    modules.value.filter((mod) => mod.internal === true && m?.[mod.key] != null).map((mod) => mod.key),
  )
  for (const mod of modules.value) {
    const v = m?.[mod.key]
    if (v == null) {
      next[mod.key] = { mode: 'none', items: [] }
    } else if (!String(v).trim() || String(v).trim() === '*' || String(v) === mod.default_scope) {
      next[mod.key] = { mode: 'all', items: [] }
    } else {
      next[mod.key] = {
        mode: 'pick',
        items: String(v)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      }
    }
  }
  state.value = next
  const unknown: Record<string, string> = {}
  for (const [k, v] of Object.entries(m ?? {})) {
    if (!knownKeys.value.has(k)) unknown[k] = v
  }
  unknownEntries.value = unknown
}

initFrom(props.modelValue ?? {})

/**
 * spec 晚到（父層還在 `ensureProducts()` 的路上就先掛了本元件）時重新初始化一次。
 * ★ 這**不是**在放寬「換產品要 bump `:key`」的約定——那條仍然必要（換產品同時也要
 *   換掉 `modelValue`，本 watch 只看得到 spec 那一半）。
 */
watch(
  () => props.spec,
  () => initFrom(props.modelValue ?? {}),
)

/**
 * 只 render 已備好內部狀態的列（spec 剛換、state 還沒跟上的那一幀不會炸 v-model）。
 * 批155：不允許勾內部模組時，內部模組只在起手值就有它時才畫（鎖住，見檔頭）。
 */
const rows = computed(() =>
  modules.value.filter(
    (m) => state.value[m.key] && (m.internal !== true || props.allowInternal || presetInternal.value.has(m.key)),
  ),
)
/** 這一列鎖住（內部模組、而這個身分不能動它）。 */
function locked(m: { internal?: boolean }): boolean {
  return m.internal === true && !props.allowInternal
}

function build(): Record<string, string> {
  // spec 還沒到＝不知道有哪些鍵 → 原樣回吐（絕不清空）。
  if (!modules.value.length) return { ...(props.modelValue ?? {}) }
  const out: Record<string, string> = {}
  for (const mod of modules.value) {
    const st = state.value[mod.key]
    if (!st || st.mode === 'none') continue
    if (mod.kind !== 'scoped' || st.mode === 'all') {
      out[mod.key] = mod.default_scope || '*'
      continue
    }
    if (st.items.length) out[mod.key] = st.items.join(',')
  }
  // 未知鍵接在後面（順序不重要，JSON 物件本來就以鍵取值）。
  return { ...out, ...unknownEntries.value }
}

watch(state, () => emit('update:modelValue', build()), { deep: true })
emit('update:modelValue', build())

/** 已停用（deprecated）的模組：畫面照列（既有授權可能帶著），但標記出來。 */
function rowClass(deprecated: boolean): Record<string, boolean> {
  return { dim: deprecated }
}
</script>

<template>
  <fieldset class="mod-box">
    <legend>模組授權</legend>
    <p v-if="!modules.length" class="mod-empty">
      這個產品還沒有定義任何模組——請先到〈產品 → 憑證設計〉新增模組鍵。
    </p>
    <div
      v-for="mod in rows"
      :key="mod.key"
      class="mod-row"
      :class="{ ...rowClass(mod.deprecated), locked: locked(mod) }"
      :title="locked(mod) ? '內部模組：只有營運主管以上能調整' : undefined"
    >
      <span class="mod-k" :class="{ long: mod.label.length > 8 }" :title="mod.key">
        {{ mod.label }}
        <small v-if="mod.deprecated" class="dep">已停用</small>
        <small v-if="mod.internal" class="int">內部</small>
      </span>
      <label>
        <input v-model="state[mod.key].mode" type="radio" :value="'none'" :disabled="locked(mod)" /> 不需要
      </label>
      <label><input v-model="state[mod.key].mode" type="radio" :value="'all'" :disabled="locked(mod)" /> 全部</label>
      <label v-if="mod.kind === 'scoped'">
        <input v-model="state[mod.key].mode" type="radio" :value="'pick'" :disabled="locked(mod)" /> 自選
      </label>
      <span v-if="mod.kind === 'scoped' && state[mod.key].mode === 'pick'" class="mod-items">
        <label
          v-for="it in mod.items"
          :key="it.key"
          :class="{ off: !it.selectable }"
          :title="it.selectable ? it.key : `${it.key}（此子項不可授權）`"
        >
          <input
            v-model="state[mod.key].items"
            type="checkbox"
            :value="it.key"
            :disabled="!it.selectable || locked(mod)"
          />
          {{ it.label }}
        </label>
        <span v-if="!mod.items.length" class="subtle">（憑證設計未列子項）</span>
      </span>
    </div>
    <p v-if="Object.keys(unknownEntries).length" class="mod-unknown">
      ⚠ 這張授權含本產品憑證設計沒有的模組代號：
      <code v-for="(v, k) in unknownEntries" :key="k" class="mono">{{ k }}={{ v }}</code>
      。已<strong>原樣保留</strong>（不會被重簽悄悄刪掉）；若是打錯字請改用套裝重新帶入。
    </p>
    <p v-if="!allowInternal && presetInternal.size" class="mod-unknown">
      標「內部」的模組只有營運主管以上能調整，這裡原樣保留；含內部模組的內容送出會被擋下，請改由營運主管處理。
    </p>
  </fieldset>
</template>

<style scoped>
.mod-box {
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  padding: 4px 14px 10px;
  margin: 0;
}
.mod-box legend {
  padding: 0 8px;
  font-size: var(--fs-12);
  color: var(--fg-3);
  font-weight: 600;
}
.mod-empty {
  margin: var(--sp-2) 0;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.mod-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  padding: 6px 0;
  border-top: 1px solid var(--border-2);
}
.mod-box .mod-row:first-of-type {
  border-top: none;
}
.mod-row.dim .mod-k {
  color: var(--fg-4);
}
.mod-k {
  /* 批87：最長的標籤是「AI 自動客服」（6 字）——64px 會被折成兩行，撐高整列。 */
  width: 76px;
  flex-shrink: 0;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.mod-k .dep {
  display: block;
  font-size: var(--fs-11);
  color: var(--warning);
}
/* 批155：內部模組的標記（與「已停用」同一個位置與字級，顏色用品牌色區分）。 */
.mod-k .int {
  display: block;
  font-size: var(--fs-11);
  color: var(--brand-600, var(--fg-3));
}
.mod-row.locked label {
  opacity: 0.55;
  cursor: not-allowed;
}
.mod-row label,
.mod-items label {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  cursor: pointer;
  user-select: none;
  transition: all var(--dur-fast) var(--ease);
}
.mod-row label:hover,
.mod-items label:hover {
  border-color: var(--brand-400);
}
.mod-items label.off {
  opacity: 0.5;
  cursor: not-allowed;
}
.mod-row label input,
.mod-items label input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.mod-row label:has(input:checked) {
  background: var(--brand-50);
  border-color: var(--brand-400);
  color: var(--brand-700);
  font-weight: 600;
}
.mod-items {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-left: 6px;
  border-left: 1px solid var(--border-2);
}
.mod-items label:has(input:checked) {
  background: rgba(124, 92, 191, 0.13);
  border-color: #b29ce0;
  color: #6a4caf;
}
.mod-unknown {
  margin: var(--sp-2) 0 0;
  font-size: var(--fs-11);
  color: var(--warning);
  line-height: var(--lh-base);
}
.mod-unknown code {
  margin: 0 3px;
}
/* 手機／平板（批171）：76px 的標籤欄放不下長名稱（「AI 功能（OCR、索引助理…）」被折成七八行）
   → 長名稱（超過 8 個字）獨佔一列、選項排在下一列；短名稱照舊並排，清單不會無端長一倍。 */
@media (--compact) {
  .mod-k.long {
    flex: 1 1 100%;
    width: auto;
  }
}
@media (--mobile) {
  .mod-box {
    padding: 4px 10px 10px;
  }
}
</style>
